// ============================================
// KB RESULT FORMATTERS
// ============================================
// Pure formatters that turn raw /api/knowledge-base responses into the
// strings the assistant model receives via function_call_output.
//
// Extracted from VoiceBotPanel.tsx in Wave 3a of the widget refactor.
// Zero side effects — these functions only return strings (and, for travel,
// the article link to attach via pendingLinksRef). All ref/state mutations
// remain in the caller.
//
// CRITICAL: Every instruction string here is byte-equivalent to the original
// inline strings in VoiceBotPanel.tsx. If you change wording, run
// `npm run prompt:check` and verify the assistant behavior end-to-end.

import { FUNCTION_OUTPUT_LANGUAGE_WRAPPER, LanguageCode } from '../config/language'
import { extractTravelDestinationHints } from '../utils/travel-hints'

export type CustomerIndustry = 'real_estate' | 'travel' | 'insurance' | string

export interface KbResult {
  id?: string | number
  title?: string
  content?: string
  category?: string
  metadata?: Record<string, any>
}

interface KbData {
  results?: KbResult[]
  count?: number
  matched_city?: string | null
  matched_region?: string | null
}

// Resolve a language wrapper or fall back to empty (matches original inline default).
const resolveLangWrapper = (language: LanguageCode) =>
  FUNCTION_OUTPUT_LANGUAGE_WRAPPER[language] || { prefix: '', suffix: '' }

// ----- Insurance ------------------------------------------------------------

export const formatInsuranceKbResults = (
  results: KbResult[],
  language: LanguageCode,
): string => {
  const insuranceInstruction =
    `INSURANCE KB RESULTS FOUND (${results.length}). You MUST answer from the results below. Do NOT say "אין לי מידע" or "לא מצאתי" when results exist. If results mention multiple policies/companies, state that briefly and ask one short clarifying question.\n\n`
  const insuranceData = results
    .map((r, idx) => `[Result ${idx + 1}] ${r.title}\n${r.content}`)
    .join('\n\n')
  const langWrapper = resolveLangWrapper(language)
  return langWrapper.prefix + insuranceInstruction + insuranceData + langWrapper.suffix
}

// ----- Travel ---------------------------------------------------------------

export interface TravelLink {
  label: string
  url: string
}

export interface FormatTravelResult {
  output: string
  articleLink: TravelLink | null
}

/**
 * Pick the best article link from travel KB results, preferring articles
 * geographically matching the query and "article"-category entries.
 *
 * If the query specifies a destination but no matching result exists, returns
 * null (sending a wrong-country link is worse than sending none).
 */
const pickTravelArticleLink = (
  results: KbResult[],
  query: string,
): { link: TravelLink | null; geoFilteredOut: boolean } => {
  const queryDestHints = extractTravelDestinationHints(query)

  const candidates = queryDestHints.length > 0
    ? results.filter((r) => {
        const rGeo = extractTravelDestinationHints(
          `${r.metadata?.country_he || ''} ${r.metadata?.country_en || ''} ${r.title || ''}`
        )
        return rGeo.length === 0 || queryDestHints.some((h) => rGeo.includes(h))
      })
    : results

  const geoFilteredOut = queryDestHints.length > 0 && candidates.length === 0

  // First pass: prefer 'article' category (detailed content with hotels, tips, etc.)
  for (const r of candidates) {
    const url = r.metadata?.url?.trim()
    const category = (r.category || '').toLowerCase()
    if (url && !url.includes('/category/') && url.startsWith('http') && category === 'article') {
      return { link: { label: r.title || 'מידע נוסף', url }, geoFilteredOut }
    }
  }
  // Second pass: any entry with a URL
  for (const r of candidates) {
    const url = r.metadata?.url?.trim()
    if (url && !url.includes('/category/') && url.startsWith('http')) {
      return { link: { label: r.title || 'מידע נוסף', url }, geoFilteredOut }
    }
  }
  return { link: null, geoFilteredOut }
}

export const formatTravelKbResults = (
  results: KbResult[],
  query: string,
  language: LanguageCode,
): FormatTravelResult => {
  const { link: articleLink, geoFilteredOut } = pickTravelArticleLink(results, query)

  if (geoFilteredOut) {
    const queryDestHints = extractTravelDestinationHints(query)
    console.log('🚫 No geo-matching articles for link — skipping link (query destination:', queryDestHints.join(', '), ')')
  }

  const travelResults = results
    .map((r, idx) => {
      const cleanContent = (r.content || '').replace(/https?:\/\/[^\s)]+/g, '').trim()
      return `[Result ${idx + 1}] ${r.title}\n${cleanContent}`
    })
    .join('\n\n')

  const langWrapper = resolveLangWrapper(language)
  const linkInstruction = articleLink
    ? `2. A clickable link to a detailed article "${articleLink.label}" was attached in chat.
3. The link leads to a full guide with more details, photos, and booking options.
4. CRITICAL: Only recommend hotels, restaurants, or places that appear in the article "${articleLink.label}" (the linked article). Do NOT recommend places from other results if they are not in the linked article.
5. At the end of your answer, mention that you sent a link to the article/guide in chat.
6. After answering, ask ONE short follow-up question.`
    : `2. Do not say you sent a link unless a clickable link was actually attached.
3. If the user asks for a specific place or link, call search_knowledge_base again.
4. After answering, ask ONE short follow-up question.`
  const travelInstruction = `TRAVEL KB RESULTS (${results.length}).

RULES:
1. You MUST answer from the results below. Do NOT say "לא מצאתי" or "אין לי מידע" or "אין לי מידע ספציפי" or "לא מצאתי תוצאה ספציפית" when results exist. Use the information in the results to give a helpful, detailed answer. If a result mentions hotels, beaches, or attractions — share that info.
${linkInstruction}
9. These results are valid only for the current query. If the user follows up with a different named hotel, restaurant, attraction, car, or explicit link request, you must call search_knowledge_base again with that exact new request. If the follow-up is vague but clearly continues the same destination/city, call search_knowledge_base again and include that active destination/city.
10. Write Hebrew with nikud (diacritics) for better pronunciation accuracy.
11. NEVER suggest the user visit any website or search online themselves. All links are provided automatically from search results — do not invent, dictate, or spell out any URL.
12. NEVER invent hotel names, restaurant names, or attraction names that do not appear in the results above.

`
  const output = langWrapper.prefix + travelInstruction + travelResults + langWrapper.suffix
  return { output, articleLink }
}

// ----- Real estate ----------------------------------------------------------

const extractRealEstateName = (title: string): string => {
  const parts = title?.split(' - ')
  if (parts && parts[1]) {
    return parts[1].split(',')[0].trim()
  }
  return title
}

// Broad city/region queries (e.g. "פרויקטים במרכז") can match many projects.
// Sending the full CONTENT of every result floods the Realtime model with
// dozens of long markdown docs — this both slows the spoken response massively
// (30s+ of "typing") and makes the model re-fire the same search in a loop.
// For large result sets we therefore send a COMPACT list (project name + city
// only, no CONTENT) and tell the model to present a few and ask which to
// explore; a targeted follow-up search then returns full CONTENT for the chosen
// project. Small result sets (specific city/project) keep full content.
const REAL_ESTATE_FULL_CONTENT_MAX = 8

export const formatRealEstateKbResults = (
  results: KbResult[],
  matchedCity: string | null | undefined,
  matchedRegion: string | null | undefined,
  language: LanguageCode,
): string => {
  const isCompact = results.length > REAL_ESTATE_FULL_CONTENT_MAX

  const exactResults = isCompact
    ? results
        .map((r, idx) => {
          const exactTitle = r.title || ''
          const exactName = extractRealEstateName(exactTitle)
          const exactCity = r.metadata?.city || exactTitle.split(',')[1]?.trim() || 'לא צוין'
          return `[Result ${idx + 1}] PROJECT_NAME_EXACT: ${exactName} | CITY_EXACT: ${exactCity} | TITLE_EXACT: ${exactTitle}`
        })
        .join('\n')
    : results
        .map((r, idx) => {
          const exactTitle = r.title || ''
          const exactName = extractRealEstateName(exactTitle)
          const exactCity = r.metadata?.city || exactTitle.split(',')[1]?.trim() || 'לא צוין'
          return `[Result ${idx + 1}]
TITLE_EXACT: ${exactTitle}
PROJECT_NAME_EXACT: ${exactName}
CITY_EXACT: ${exactCity}
CONTENT:
${r.content}`
        })
        .join('\n\n')

  const langWrapper = resolveLangWrapper(language)
  const matchedCityLine = matchedCity ? `CITY_FILTER_REQUESTED: ${matchedCity}\n` : ''
  const matchedRegionLine = matchedRegion ? `REGION_FILTER_REQUESTED: ${matchedRegion}\n` : ''
  const compactNote = isCompact
    ? `LIST_MODE: COMPACT — ${results.length} projects matched, so only PROJECT_NAME_EXACT + CITY_EXACT are shown (no full details).
8. This list already contains ALL matching projects — do NOT call search_knowledge_base again for the same city/region, and do NOT read the whole list aloud.
9. Present just 2-3 relevant project names (you may group them by city) and ask the customer which one to explore.
10. When the customer picks a project, call search_knowledge_base again with that exact project name to get its full details.
`
    : ''
  const instruction = `REAL ESTATE KB RESULTS FOUND (${results.length}).
CRITICAL RULES:
1. You may mention ONLY projects from PROJECT_NAME_EXACT lines below.
2. Never invent a new project name.
3. Never change a project's city and never combine one project's name with another city's name.
4. When the customer asks which projects exist in a city, mention ONLY results whose CITY_EXACT matches that city exactly.
5. If there is no exact city match in the results, say you do not currently have an exact project result for that city.
6. Prefer quoting PROJECT_NAME_EXACT exactly as written.
7. Do not use outside knowledge or memory.
${matchedCityLine}${matchedRegionLine}${compactNote}
`

  return langWrapper.prefix + instruction + exactResults + langWrapper.suffix
}

// ----- Empty results --------------------------------------------------------

export const formatEmptyKbResults = (industry: CustomerIndustry): string => {
  if (industry === 'insurance') {
    return 'NO INSURANCE RESULTS FOUND. Ask one short clarifying question (company/policy/topic) and offer to connect to a representative if needed.'
  }
  if (industry === 'travel') {
    return 'NO TRAVEL RESULTS FOUND. Ask the caller to clarify which destination or topic they are interested in. Offer popular destinations like Greece, Italy, Dubai, or Israel.'
  }
  return 'NO PROPERTIES FOUND. Ask customer to clarify what they\'re looking for.'
}

// ----- Top-level dispatcher -------------------------------------------------

export interface FormatKbForBotResult {
  output: string
  articleLink: TravelLink | null
}

/**
 * Format any KB response (with or without results) for the assistant model.
 * Returns the function output string + the optional travel article link
 * the caller should attach to pendingLinksRef.
 */
export const formatKbForBot = (
  kbData: KbData,
  effectiveQuery: string,
  industry: CustomerIndustry,
  language: LanguageCode,
): FormatKbForBotResult => {
  const results = kbData.results
  if (!results || results.length === 0) {
    return { output: formatEmptyKbResults(industry), articleLink: null }
  }

  if (industry === 'insurance') {
    return { output: formatInsuranceKbResults(results, language), articleLink: null }
  }
  if (industry === 'travel') {
    return formatTravelKbResults(results, effectiveQuery, language)
  }
  return {
    output: formatRealEstateKbResults(results, kbData.matched_city, kbData.matched_region, language),
    articleLink: null,
  }
}
